'use client'

import { useState, useEffect, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Plus } from 'lucide-react'
import { columns } from './columns'
import { DataTable } from './data-table'
import { useExaminationRooms } from '@/hooks/useExaminationRooms'
import { Skeleton } from '@/components/ui/skeleton'
import { ExaminationRoomCreateDialog } from './ExaminationRoomCreateDialog'
import { ExaminationRoomDialog } from './ExaminationRoomDialog'
import { useViewMode } from '@/stores/viewMode'

export default function ExaminationRoomsPage() {
  const { id: clinicId } = useViewMode()

  const [page] = useState(1)
  const [pageSize] = useState(10)
  const [department, setDepartment] = useState('')
  const [dialogOpen, setDialogOpen] = useState(false)

  // 外部控制的診間對話框狀態
  const [externalDialogOpen, setExternalDialogOpen] = useState(false)
  const [selectedExaminationRoomId, setSelectedExaminationRoomId] = useState<number | undefined>()

  const { examinationRooms, isLoading, isError, mutate } = useExaminationRooms({
    page,
    pageSize,
    clinic_id: clinicId,
    department,
  })

  const handleSearch = useCallback((value: string) => {
    setDepartment(value)
  }, [])

  // 使用 window.location 來讀取 URL 參數
  const getUrlParams = () => {
    if (typeof window === 'undefined') return { examinationId: null, pageParam: null }

    const searchParams = new URLSearchParams(window.location.search)
    return {
      examinationId: searchParams.get('e'),
      pageParam: searchParams.get('p'),
    }
  }

  // 監聽URL參數變化，自動打開對應的診間對話框
  useEffect(() => {
    const { examinationId } = getUrlParams()

    if (examinationId && examinationRooms && examinationRooms.length > 0) {
      const targetRoom = examinationRooms.find(
        (room) => room.examination_room_id === parseInt(examinationId),
      )

      if (targetRoom) {
        setSelectedExaminationRoomId(targetRoom.examination_room_id)
        setExternalDialogOpen(true)
      }
    }
  }, [examinationRooms])

  // 關閉外部對話框時清除URL參數
  const handleExternalDialogClose = (open: boolean) => {
    setExternalDialogOpen(open)
    if (!open) {
      setSelectedExaminationRoomId(undefined)
      // 清除URL參數
      if (typeof window !== 'undefined') {
        const url = new URL(window.location.href)
        url.searchParams.delete('e')
        url.searchParams.delete('p')
        window.history.replaceState({}, '', url.pathname + (url.search || ''))
      }
    }
  }

  if (isError) {
    return (
      <div>
        <div className="text-center text-destructive">載入診間列表失敗。請稍後再試。</div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div>
        <Skeleton className="h-8 w-[200px] mb-4" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">診間管理</h2>
          <p className="text-muted-foreground mt-2">管理診間資訊與版型設定</p>
        </div>
        <Button onClick={() => setDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          新增診間
        </Button>
      </div>

      <Card>
        <CardContent className="p-6">
          <DataTable
            columns={columns}
            data={examinationRooms || []}
            onSearch={handleSearch}
            meta={{
              reloadData: () => mutate(),
              mutate,
            }}
          />
        </CardContent>
      </Card>

      {/* 新增診間對話框 */}
      <ExaminationRoomCreateDialog
        open={dialogOpen}
        onSuccess={() => mutate()}
        onOpenChange={setDialogOpen}
        clinicId={clinicId}
      />

      {/* 外部控制的診間設定對話框 */}
      <ExaminationRoomDialog
        open={externalDialogOpen}
        onOpenChange={handleExternalDialogClose}
        clinicId={clinicId}
        examinationRoomId={selectedExaminationRoomId}
        onMutate={() => mutate()}
      />
    </div>
  )
}
